import { useState } from "react";
import { Button, cn, Input } from "@carry/ui";
import { formatYen } from "@carry/shared";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import { ReportCard, ReportSectionTitle } from "../components/ReportCard";
import {
  createDriverRefueling,
  deleteDriverRefueling,
  updateDriverRefueling,
  uploadDriverRefuelingReceipt,
  useDriverRefuelings,
  type CreateRefuelingInput,
  type DriverFetcher,
  type RefuelingDto,
} from "../hooks";
import { enqueueAction } from "../offline-queue";
import { formatHm } from "../lib/normalize";

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function toJstDateTimeLocal(value: string): string {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return "";
  return new Date(instant.getTime() + JST_OFFSET_MS).toISOString().slice(0, 16);
}

export function RefuelingScreen({
  fetcher,
  onBack,
  onChanged,
}: {
  fetcher: DriverFetcher;
  onBack: () => void;
  onChanged?: () => void | Promise<void>;
}) {
  const list = useDriverRefuelings({
    apiBaseUrl: fetcher.apiBaseUrl,
    session: fetcher.session,
    fetchImpl: fetcher.fetchImpl,
  });
  const [liters, setLiters] = useState("");
  const [amount, setAmount] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState<RefuelingDto | null>(null);
  const [editLiters, setEditLiters] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editRefueledAt, setEditRefueledAt] = useState("");
  const [mutationPending, setMutationPending] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [mutationSuccess, setMutationSuccess] = useState<string | null>(null);
  const current = list.refuelings ?? [];
  const full = current.length >= 3;

  const startEditing = (refueling: RefuelingDto) => {
    setEditing(refueling);
    setEditLiters(refueling.liters);
    setEditAmount(refueling.amount.toString());
    setEditRefueledAt(toJstDateTimeLocal(refueling.refueled_at));
    setMutationError(null);
    setMutationSuccess(null);
  };

  const handleUpdate = async () => {
    if (!editing) return;
    const parsedAmount = parseInt(editAmount.replace(/[^\d]/g, ""), 10);
    if (!editLiters.trim() || !/^[0-9]+(\.[0-9]+)?$/.test(editLiters.trim())) {
      setMutationError("リッター数を正しく入力してください");
      return;
    }
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setMutationError("金額を正しく入力してください");
      return;
    }
    setMutationPending(true);
    setMutationError(null);
    setMutationSuccess(null);
    try {
      await updateDriverRefueling(fetcher, editing.public_id, {
        liters: editLiters.trim(),
        amount: parsedAmount,
        refueled_at: `${editRefueledAt}:00+09:00`,
      });
      await list.refetch();
      setEditing(null);
      setMutationSuccess("給油を変更しました");
      await onChanged?.();
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : String(error));
    } finally {
      setMutationPending(false);
    }
  };

  const handleDelete = async (refueling: RefuelingDto) => {
    if (
      !confirm(
        `給油 #${refueling.sequence}（${refueling.liters} L・${formatYen(refueling.amount)}）を削除しますか？`,
      )
    )
      return;
    setMutationPending(true);
    setMutationError(null);
    setMutationSuccess(null);
    try {
      await deleteDriverRefueling(fetcher, refueling.public_id);
      await list.refetch();
      if (editing?.public_id === refueling.public_id) setEditing(null);
      setMutationSuccess("給油を削除しました");
      await onChanged?.();
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : String(error));
    } finally {
      setMutationPending(false);
    }
  };

  const handleSubmit = async () => {
    const l = liters.trim();
    const a = parseInt(amount.replace(/[^\d]/g, ""), 10);
    if (!l || !/^[0-9]+(\.[0-9]+)?$/.test(l)) {
      setSubmitError("リッター数を正しく入力してください");
      return;
    }
    if (!Number.isFinite(a) || a <= 0) {
      setSubmitError("金額を正しく入力してください");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const input: CreateRefuelingInput = {
        liters: l,
        amount: a,
        refueled_at: new Date().toISOString(),
      };
      let created: RefuelingDto;
      try {
        created = await createDriverRefueling(fetcher, input);
      } catch (e) {
        // オフライン時は給油本体のみキュー化 (領収書画像はキュー不可)
        enqueueAction({
          kind: "create-refueling",
          path: "/api/v1/driver/refuelings",
          method: "POST",
          body: input,
        });
        throw e;
      }
      // 領収書があればアップロード (本体作成は成功済みのため、画像失敗は警告に留める)
      if (receiptFile) {
        try {
          await uploadDriverRefuelingReceipt(fetcher, created.public_id, receiptFile);
        } catch (e) {
          setSubmitError(
            `給油は記録しましたが領収書のアップロードに失敗しました: ${
              e instanceof Error ? e.message : String(e)
            }`,
          );
        }
      }
      setSubmitted(true);
      setLiters("");
      setAmount("");
      setReceiptFile(null);
      await list.refetch();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="給油記録" sub={`本日 ${current.length} / 3 件`} showBack onBack={onBack} />
      <div className="px-3 py-3.5">
        <ReportSectionTitle label="本日の記録" hint={`${current.length}/3`} />
        <div>
          {list.loading && !list.refuelings && (
            <div className="p-4 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
              読込中…
            </div>
          )}
          {list.error && (
            <div className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
              ⚠ {list.error}
            </div>
          )}
          {current.length === 0 && !list.loading && (
            <div className="rounded-lg bg-[var(--bg-raised)] p-3 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
              記録はありません
            </div>
          )}
          {current.map((r: RefuelingDto) => (
            <div
              key={r.public_id}
              className="mb-1.5 flex items-center justify-between rounded-lg bg-[var(--bg-raised)] px-3.5 py-2.5 shadow-sm"
            >
              <div>
                <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                  #{r.sequence} · {formatHm(r.refueled_at)}
                </div>
                <div className="num text-[length:var(--fs-sm)]">{r.liters} L</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="num text-[length:var(--fs-md)] font-semibold">
                  {formatYen(r.amount)}
                </div>
                <div className="flex gap-1">
                  <Button type="button" variant="outline" onClick={() => startEditing(r)}>
                    変更
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={mutationPending}
                    onClick={() => void handleDelete(r)}
                  >
                    削除
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <>
            <ReportSectionTitle label="給油を編集" className="mt-3.5" />
            <ReportCard>
              <div className="grid grid-cols-2 gap-2.5">
                <Input
                  aria-label="編集するリッター数"
                  type="text"
                  inputMode="decimal"
                  value={editLiters}
                  onChange={(event) => setEditLiters(event.target.value)}
                />
                <Input
                  aria-label="編集する金額"
                  type="text"
                  inputMode="numeric"
                  value={editAmount}
                  onChange={(event) => setEditAmount(event.target.value)}
                />
              </div>
              <Input
                aria-label="給油時刻"
                type="datetime-local"
                value={editRefueledAt}
                onChange={(event) => setEditRefueledAt(event.target.value)}
                className="mt-2.5"
              />
              <div className="mt-2.5 flex gap-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  キャンセル
                </Button>
                <Button
                  type="button"
                  disabled={mutationPending}
                  onClick={() => void handleUpdate()}
                >
                  変更を保存
                </Button>
              </div>
            </ReportCard>
          </>
        )}

        {!full && (
          <>
            <ReportSectionTitle label="新規登録" className="mt-3.5" />
            <ReportCard>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <div className="mb-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                    リッター (L)
                  </div>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={liters}
                    onChange={(e) => setLiters(e.target.value)}
                    placeholder="35.20"
                    className="num h-auto py-2.5 text-right text-base"
                  />
                </div>
                <div>
                  <div className="mb-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                    金額 (円)
                  </div>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="6280"
                    className="num h-auto py-2.5 text-right text-base"
                  />
                </div>
              </div>
              <label className="mt-2.5 flex cursor-pointer flex-col gap-2">
                <span className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                  領収書 (任意)
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
                  className="text-[length:var(--fs-sm)] text-[var(--ink-2)] file:mr-2 file:cursor-pointer file:rounded-md file:border file:border-[var(--rule-soft)] file:bg-[var(--bg-raised)] file:px-3 file:py-1.5 file:text-[var(--ink-2)]"
                />
                {receiptFile && (
                  <span className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                    {receiptFile.name}（{Math.round(receiptFile.size / 1024)}KB）
                  </span>
                )}
              </label>
            </ReportCard>
          </>
        )}

        {full && (
          <div className="mt-3.5 rounded-lg bg-[var(--st-done-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
            本日の給油記録は 3 件に達しました
          </div>
        )}

        {submitError && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {submitError}
          </div>
        )}
        {mutationError && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            <span aria-hidden="true">⚠ </span>
            <span>{mutationError}</span>
          </div>
        )}
        {mutationSuccess && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-done-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
            ✓ {mutationSuccess}
          </div>
        )}
        {submitted && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-done-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
            ✓ 給油を記録しました
          </div>
        )}
      </div>
      <BottomBar>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="h-auto min-h-[var(--row-h)] px-3.5 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
        >
          戻る
        </Button>
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={submitting || full}
          className={cn(
            "h-auto min-h-[var(--row-h)] flex-1 bg-[var(--ok)] px-4 py-3 text-[length:var(--fs-md)] font-semibold text-white hover:bg-[var(--ok)]/90",
            (submitting || full) && "opacity-60",
            submitting ? "cursor-wait" : full ? "cursor-not-allowed" : "",
          )}
        >
          {submitting ? "送信中…" : full ? "満件 (3/3)" : "給油を記録"}
        </Button>
      </BottomBar>
    </div>
  );
}
